import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const parseEmails = (s) => Array.from(new Set(s.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x))));

/* State + API calls behind the lead-document share dialog (link generation, email, active links). */
export default function useShareLead(lead, open) {
  const leadId = lead?.id;
  const hasDocs = (lead?.documents || []).length > 0;
  const [days, setDays] = useState(7);
  const [link, setLink] = useState(null);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const loadLinks = useCallback(() => {
    if (leadId) api.get(`/leads/${leadId}/share`).then((r) => setLinks(r.data)).catch(() => {});
  }, [leadId]);

  const generate = useCallback(async (d) => {
    setBusy(true);
    try { const r = await api.post(`/leads/${leadId}/share`, { expires_in_days: d }); setLink(r.data); loadLinks(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }, [leadId, loadLinks]);

  useEffect(() => {
    if (!open || !leadId) return;
    setLink(null); setEmailResult(null);
    if (hasDocs) generate(7);
    loadLinks();
  }, [open, leadId, hasDocs, generate, loadLinks]);

  const changeDays = (d) => { setDays(d); generate(d); };

  const sendEmail = async (emails, message) => {
    const to = parseEmails(emails);
    if (!to.length) { toast.error("Enter at least one valid email address"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/share/email`, { to, message: message || null, expires_in_days: days });
      setEmailResult(r.data); loadLinks();
      toast.success(`Email sent to ${r.data.sent.length} recipient(s)`);
    } catch (err) {
      const fallback = err?.response?.headers?.["x-share-url"];
      if (fallback) setLink((l) => l || { url: fallback, expires_at: null, whatsapp_text: null });
      toast.error(formatApiError(err), { duration: 9000 });
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/leads/${leadId}/share/${id}`); toast.success("Link revoked"); loadLinks(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return { days, changeDays, link, links, busy, emailResult, sendEmail, revoke };
}
